/**
 * 🚀 LAYOUT SELECTOR 2025 - Revolutionärer Layout-Selektor
 * 
 * Ersetzt den alten LayoutSelector mit:
 * - Dynamische Layout-Generierung aus LayoutEngine
 * - Einheitliche API für alle Widget-Types
 * - Optimierte Preview-Generation
 * - Bessere Performance
 */

import React, { useMemo } from 'react';
import LayoutEngine, { WIDGET_LAYOUTS } from '../core/LayoutEngine';
import './LayoutSelector2025.css';

const LayoutSelector2025 = ({ 
  widgetType = 'heroHeader',
  selectedLayout = null,
  onLayoutChange,
  showPreview = true,
  className = ''
}) => {
  
  // Verfügbare Layouts für Widget-Type
  const availableLayouts = useMemo(() => {
    const layouts = WIDGET_LAYOUTS[widgetType];
    if (!layouts) return [];
    
    return Object.entries(layouts).map(([layoutId, config]) => ({
      id: layoutId,
      name: formatLayoutName(layoutId),
      description: generateDescription(config),
      config,
      preview: generatePreview(config)
    }));
  }, [widgetType]);
  
  // Layout Selection Handler
  const handleLayoutSelect = (layoutId) => {
    const layout = availableLayouts.find(l => l.id === layoutId);
    if (layout && onLayoutChange) {
      onLayoutChange(layoutId, layout.config);
    }
  };
  
  // Auto-select first layout if none selected
  const currentLayout = selectedLayout || (availableLayouts[0]?.id);
  
  if (availableLayouts.length === 0) {
    return (
      <div className="layout-selector-2025 no-layouts">
        <p>No layouts available for {widgetType}</p>
      </div>
    );
  }
  
  return (
    <div className={`layout-selector-2025 ${className}`}>
      {/* Header */}
      <div className="layout-selector-header">
        <h3 className="layout-selector-title">
          <svg className="layout-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3h7v7H3V3zm0 11h7v7H3v-7zm11-11h7v7h-7V3zm0 11h7v7h-7v-7z"/>
          </svg>
          Layout Variations
        </h3>
        <p className="layout-selector-description">
          Choose how your content is arranged ({availableLayouts.length} options)
        </p>
      </div>
      
      {/* Layout Grid */}
      <div className="layout-options-grid">
        {availableLayouts.map((layout) => (
          <div
            key={layout.id}
            className={`layout-option ${currentLayout === layout.id ? 'selected' : ''}`}
            onClick={() => handleLayoutSelect(layout.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLayoutSelect(layout.id);
              }
            }}
          >
            {/* Preview */}
            {showPreview && (
              <div className="layout-preview">
                <div className={`layout-preview-content ${layout.id}`}>
                  {renderPreview(layout)}
                </div>
              </div>
            )}
            
            {/* Info */}
            <div className="layout-info">
              <h4 className="layout-name">{layout.name}</h4>
              <p className="layout-description">{layout.description}</p>
            </div>
            
            {/* Selected Indicator */}
            {currentLayout === layout.id && (
              <div className="layout-selected-indicator">
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Formatiert Layout-Namen für UI
 */
function formatLayoutName(layoutId) {
  return layoutId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Generiert Beschreibung aus Layout-Config
 */
function generateDescription(config) {
  const { pattern, textSection, imageSection, features } = config;
  
  // Pattern-basierte Beschreibungen
  const patternDescriptions = {
    'horizontal-split': 'Side-by-side layout',
    'horizontal-split-reverse': 'Reversed side-by-side',
    'vertical-stack': 'Stacked vertically',
    'overlay-centered': 'Text over background',
    'responsive-grid': 'Responsive grid layout',
    'full-width': 'Full width layout'
  };
  
  let description = patternDescriptions[pattern] || 'Custom layout';
  
  // Zusätzliche Feature-Info
  if (features?.compactText) description += ', compact text';
  if (features?.roundButton) description += ', round button';
  if (features?.minimal) description += ', minimal design';
  
  return description;
}

/**
 * Generiert Preview-Symbol
 */
function generatePreview(config) {
  const { pattern } = config;
  
  const previewMap = {
    'horizontal-split': '▐▌',
    'horizontal-split-reverse': '▌▐', 
    'vertical-stack': '▐\n▐',
    'overlay-centered': '▓▓▓',
    'responsive-grid': '▢▢\n▢▢',
    'full-width': '━━━━'
  };
  
  return previewMap[pattern] || '▐▌';
}

/**
 * Rendert Preview-Komponente
 */
function renderPreview(layout) {
  const { pattern, textSection, imageSection } = layout.config;
  
  switch (pattern) {
    case 'horizontal-split':
    case 'horizontal-split-reverse':
      return (
        <>
          <div className="preview-section preview-text" style={{ 
            flex: textSection?.flex || 1,
            order: pattern === 'horizontal-split-reverse' ? 2 : 1
          }} />
          <div className="preview-section preview-image" style={{ 
            flex: imageSection?.flex || 1,
            order: pattern === 'horizontal-split-reverse' ? 1 : 2
          }} />
        </>
      );
      
    case 'vertical-stack':
      return (
        <div className="preview-stack">
          <div className="preview-section preview-image small" />
          <div className="preview-section preview-text" />
        </div>
      );
      
    case 'overlay-centered':
      return (
        <div className="preview-overlay">
          <div className="preview-section preview-background" />
          <div className="preview-section preview-text-overlay" />
        </div>
      );
      
    case 'responsive-grid':
      return (
        <div className="preview-grid">
          <div className="preview-section preview-grid-item" />
          <div className="preview-section preview-grid-item" />
          <div className="preview-section preview-grid-item" />
        </div>
      );
      
    default:
      return <div className="preview-section preview-default" />;
  }
}

export default LayoutSelector2025;
